<template>
  <form class="user-register-form" ref="registerForm" @submit.prevent="validateForm">
    <div class="user-register-form__group-wrapper">
      <div
        v-for="(field, key) in fields"
        :key="key"
        class="user-register-form__group"
        :class="[fields[key].class]"
      >
        <input
          v-if="isShown(key) && !field.hidden"
          :type="fields[key].type"
          :class="['ine-input', fieldHasError(key) && 'ine-input--error']"
          :placeholder="fields[key].placeholder"
          :autocomplete="fields[key].autocomplete"
          @blur="handleInputBlur(key)"
          @focus="clearError(key)"
          v-model="fields[key].value"
        />
        <span v-if="fieldHasError(key)" class="ine-input__error-msg">{{ errors[key] }}</span>
      </div>
      <div class="user-register-form__password-wrapper">
        <p class="user-register-form__password-title">Password requirements</p>
        <PasswordVerifierWidget
          :password="fields.password.value"
          :isPasswordConfirmed="isPasswordConfirmed"
        />
      </div>
    </div>
    <div class="user-register-form__btn-wrapper">
      <p class="user-register-form__tos">
        By submitting this form I am agreeing for all my information to be stored and receive
        advertising from INE and from the partner companies of INE. I can revoke my consent at any
        time by sending an email to
        <a class="user-register-form__tos--link" href="mailto:support@ine.com">support@ine.com</a>.
        In addition, a link to unsubscribe is included in each email.
      </p>
      <button
        v-if="!isLoading"
        class="btn btn--full-mobile btn--primary user-register-form__btn"
        type="submit"
        :disabled="!isUserPasswordValid"
      >
        Continue
      </button>
      <vue-loaders v-else name="ball-clip-rotate" color="#ef7d22"></vue-loaders>
    </div>
  </form>
</template>

<script>
import { validateForm, clearFormErrors } from '@/helpers'
import PasswordVerifierWidget from '@/components/PasswordVerifierWidget.vue'
import { isPasswordValid } from '@/helpers/isPasswordValid'
import { emailValidation } from '@/helpers/emailValidation'

export default {
  name: 'RegistrationForm',
  components: {
    PasswordVerifierWidget,
  },
  props: {
    isAuthenticated: {
      type: Boolean,
      required: true,
    },
    profile: {
      type: Object,
      required: false,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      errors: clearFormErrors(),
      fields: {
        email: {
          type: 'email',
          placeholder: 'Email',
          rules: ['presence'],
          value: '',
          autocomplete: 'email',
          hidden: false,
        },
        'first name': {
          type: 'text',
          placeholder: 'First Name',
          rules: ['presence'],
          value: '',
          autocomplete: 'given-name',
          class: 'user-register-form__first-name',
          hidden: false,
        },
        'last name': {
          type: 'text',
          placeholder: 'Last Name',
          rules: ['presence'],
          value: '',
          autocomplete: 'family-name',
          class: 'user-register-form__last-name',
          hidden: false,
        },
        password: {
          type: 'password',
          placeholder: 'Password',
          rules: ['presence', 'password'],
          value: '',
          autocomplete: 'new-password',
          hidden: false,
        },
        'password confirmation': {
          type: 'password',
          rules: ['presence'],
          placeholder: 'Confirm Password',
          value: '',
          autocomplete: 'new-password',
          hidden: false,
        },
      },
      isUserPasswordValid: false,
    }
  },
  watch: {
    fields: {
      handler(fields) {
        const password = fields.password.value
        this.isUserPasswordValid =
          isPasswordValid({ value: password, requiredLength: 8 }) && this.isPasswordConfirmed
      },
      deep: true,
    },
  },
  computed: {
    isPasswordConfirmed() {
      return (
        this.fields.password.value.length > 0 &&
        this.fields.password.value === this.fields['password confirmation'].value
      )
    },
    loggedInUser() {
      if (this.isAuthenticated && this.profile && this.profile) {
        const {
          profile: { email, first_name: firstName, last_name: lastName },
        } = this
        return { email, firstName, lastName }
      }
      return {
        email: '',
        firstName: '',
        lastName: '',
      }
    },
  },
  methods: {
    isShown(key) {
      const known = ['password', 'password confirmation']
      if (this.isAuthenticated) {
        return !known.includes(key)
      }
      return key && key.length
    },
    fieldHasError(field) {
      return !!(this.errors[field] && this.errors[field].length)
    },
    validateForm() {
      if (Object.keys(this.errors).length > 0) return false
      const { fields } = this
      if (this.isAuthenticated) {
        delete fields.password
        delete fields['password confirmation']
      }
      const { isValid, errors } = validateForm(fields)
      this.errors = errors
      if (Object.keys(errors).length) {
        this.$refs.registerForm.scrollIntoView()
      }
      if (isValid) {
        const {
          fields: {
            email: { value: email },
            'first name': { value: first_name },
            'last name': { value: last_name },
            password: { value: password },
          },
        } = this
        const payload = {
          email,
          first_name,
          last_name,
          password,
        }
        this.$emit('submit-form', payload)
      }
    },
    updateValue(event) {
      const { value, currentValue } = event
      this[value] = currentValue
    },
    handleInputBlur(field) {
      const { fields } = this
      if (field === fields.email.type) {
        const emailValue = fields.email.value
        const { isValid, errors } = emailValidation(emailValue)
        if (!isValid) {
          this.errors = errors
        }
      }
    },
    clearError(field) {
      this.errors = Object.entries(this.errors).reduce((acc, [key, val], _) => {
        if (key !== field) {
          acc[key] = val
        }
        return acc
      }, {})
    },
  },
  created() {
    const { email, firstName, lastName } = this.loggedInUser
    this.fields.email.value = email
    this.fields['first name'].value = firstName
    this.fields['last name'].value = lastName
  },
}
</script>

<style lang="scss" scoped>
.user-register-form {
  width: 100%;
  font-family: var(--font-regular);
  display: flex;
  flex-direction: column;
  &__group-wrapper {
    width: 100%;
    transition: 0.5s all ease;
    display: flex;
    flex-wrap: wrap;
  }
  &__group {
    position: relative;
    margin: 0 auto 10px auto;
    width: 100%;
  }
  &__tos {
    font-size: rem(10px);
    line-height: 12px;
    color: var(--light-blue-gray);
    margin: 0;
    margin-right: 20px;
    &--link {
      color: var(--light-blue-gray);
    }
  }
  &__btn-wrapper {
    margin-top: 30px;
    width: 100%;
    display: flex;
    flex-direction: column-reverse;
    button {
      margin-bottom: 30px;
    }
    @include mobile {
      margin-top: 50px;
      width: unset;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      button {
        margin-bottom: unset;
      }
    }
  }
  &__password-title {
    margin: 30px 0 15px 0;
    color: var(--light-blue-gray);
    font-size: rem(12px);
  }
  @include desktop {
    &__first-name,
    &__last-name {
      width: 50%;
    }
    &__first-name {
      padding-right: 5px;
    }
    &__last-name {
      padding-left: 5px;
    }
  }
  ::v-deep .ine-form__tagline {
    color: var(--light-blue-gray);
    margin-bottom: 50px;
  }
  ::v-deep .password-verifier {
    padding: 0;
  }
}
</style>
